import { useDeleteProductMutation, useToggleProductFieldMutation, useDuplicateProductMutation } from '@services/product/product/product.endpoints';
import { ProductTable } from '@services/product/product/product.types';
import { PaginationMetadata } from '@services/providers/cosem.types';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

export const useListPageVM = () => {

    const [totalProducts, setTotalProducts] = useState<number>(0);
    const columnHelper = createColumnHelper<ProductTable>();
    const [needReload, setNeedReload] = useState<boolean>();
    const navigate = useNavigate();

    const MySwal = withReactContent(Swal)

    const [toggleProductField, { isSuccess: isToggleProductFieldSuccess, isLoading: isToggleProductFieldLoading}] = useToggleProductFieldMutation();
    const [deleteProduct, { isSuccess: isDeleteProductSuccess, isLoading: isDeleteProductLoading}] = useDeleteProductMutation();
    const [duplicateProduct, { isSuccess: isDuplicateProductSuccess, data: duplicateProductResponse, isLoading: isDuplicateProductLoading}] = useDuplicateProductMutation();

    useEffect(() => {
        if (isToggleProductFieldSuccess) {
            setNeedReload((prev) => !prev);
        }
    }, [isToggleProductFieldSuccess]);

    useEffect(() => {
        if (isDeleteProductSuccess) {
            setNeedReload((prev) => !prev);
        }
    }, [isDeleteProductSuccess]);

    useEffect(() => {
        if (isDuplicateProductSuccess && duplicateProductResponse) {
            navigate(`/products-management/products/${duplicateProductResponse.data.id}/edit`);
        }
    }, [isDuplicateProductSuccess, duplicateProductResponse, navigate]);


    const handleMetadata = (metadata: PaginationMetadata) => {
        if (metadata && metadata.total !== undefined) {
            setTotalProducts(metadata.total);
        }
    };

    const handleDeleteProduct = (id: number) => {
        MySwal.fire({
            title: 'Êtes-vous sûr de vouloir supprimer ce produit ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#5D4B99',
            confirmButtonColor: '#5D4B99',
            confirmButtonText: 'Supprimer',
            cancelButtonText: 'Annuler',
        }).then((result) => {
            if (result.isConfirmed) {
                deleteProduct({ id });
            }
        })
    }

    const handleDeleteProducts = (lines: ProductTable[]) => {
        MySwal.fire({
            title: 'Êtes-vous sûr de vouloir supprimer ces produits ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#5D4B99',
            confirmButtonColor: '#5D4B99',
            confirmButtonText: 'Supprimer',
            cancelButtonText: 'Annuler',
        }).then((result) => {
            if (result.isConfirmed) {
                lines.forEach((line) => {
                    deleteProduct({ id: line.id });
                });
            }
        })
    }

    const handleDuplicateProduct = (id: number) => {
        MySwal.fire({
            title: 'Êtes-vous sûr de vouloir dupliquer ce produit ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#5D4B99',
            confirmButtonColor: '#5D4B99',
            confirmButtonText: 'Dupliquer',
            cancelButtonText: 'Annuler',
        }).then((result) => {
            if (result.isConfirmed) {
                duplicateProduct({ id });
            }
        })
    }

    const handleToggleProductField = (id: number, field: 'isFeatured' | 'isDisabled', value: boolean) => {
        MySwal.fire({
            title: !value ? 'Êtes-vous sûr de vouloir ' + (field === 'isFeatured' ? 'mettre à la une' : 'désactiver') + ' ce produit ?' : 'Êtes-vous sûr de vouloir ' + (field === 'isFeatured' ? 'retirer de la une' : 'activer') + ' ce produit ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#5D4B99',
            confirmButtonColor: '#5D4B99',
            confirmButtonText: !value ? (field === 'isFeatured' ? 'Mettre à la une' : 'Désactiver') : (field === 'isFeatured' ? 'Retirer de la une' : 'Activer'),
            cancelButtonText: 'Annuler',
        }).then((result) => {
            if (result.isConfirmed) {
                toggleProductField({ id, field });
            }
        })
    }

    return {
        totalProducts,
        setTotalProducts,
        columnHelper,
        needReload,
        navigate,
        handleToggleProductField,
        handleMetadata,
        handleDeleteProduct,
        handleDuplicateProduct,
        handleDeleteProducts,
        isLoading: isToggleProductFieldLoading || isDeleteProductLoading || isDuplicateProductLoading,
    };
};
